import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MURALS, ARTWORKS, getMeta, FAMILY_ARCHIVE } from "../data/site";
import { useCurrency } from "../contexts/CurrencyContext";

const DEARFIELD = MURALS.works.find((m) => m.id === "dearfield-jackson-lake");
const LADY = ARTWORKS["black-western"].items.find((a) => a.id === "lady-in-green");

export default function StudioNews() {
  const { format: fmt } = useCurrency();
  const ladyMeta = getMeta(LADY.id);
  return (
    <section className="border-t border-[hsl(var(--rule))]" data-testid="studio-news">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <p className="label mb-8 md:mb-12">Recent · From the studio</p>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <article className="lg:col-span-7" data-testid="news-dearfield">
            <Link to="/murals" className="block group">
              <div className="aspect-[3/2] overflow-hidden bg-[hsl(var(--surface))]">
                <img
                  src={DEARFIELD.image}
                  alt={`${DEARFIELD.title} mural unveiling with Governor Jared Polis`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                />
              </div>
            </Link>
            <p className="label mt-5 text-[hsl(var(--ink-soft))]">
              Public Art · Colorado 150 · {DEARFIELD.year}
            </p>
            <h2 className="serif text-3xl md:text-5xl tracking-tight font-light leading-[1.05] mt-2">
              Governor Polis unveils{" "}
              <span className="italic text-[hsl(var(--ink-soft))]">Dearfield</span> at Jackson Lake State Park.
            </h2>
            {DEARFIELD.press && (
              <blockquote className="mt-5 max-w-2xl">
                <p className="serif italic text-lg md:text-xl text-foreground/90 leading-snug">
                  “{DEARFIELD.press.quote}”
                </p>
                <footer className="mt-2 label text-[hsl(var(--ink-soft))]">— {DEARFIELD.press.attribution}</footer>
              </blockquote>
            )}
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
              <Link to="/murals" className="inline-flex items-center gap-2 label link-underline whitespace-nowrap" data-testid="news-dearfield-murals-link">
                See the mural <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/journal/dearfield-colorado-150" className="inline-flex items-center gap-2 label link-underline whitespace-nowrap" data-testid="news-dearfield-journal-link">
                Read the story <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              {DEARFIELD.press && (
                <a href={DEARFIELD.press.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 label link-underline whitespace-nowrap text-[hsl(var(--ink-soft))]" data-testid="news-dearfield-press-link">
                  {DEARFIELD.press.outlet} <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </article>

          <article className="lg:col-span-5" data-testid="news-lady-in-green">
            <Link to={`/work/black-western/${LADY.id}`} className="block group">
              <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
                <img
                  src={LADY.image}
                  alt={LADY.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                />
                <span className="absolute top-4 left-4 inline-block bg-[hsl(var(--accent))] text-background px-2.5 py-1 text-[0.625rem] uppercase tracking-[0.18em]">
                  New · Black Western
                </span>
              </div>
            </Link>
            <div className="mt-5 flex items-baseline justify-between gap-4">
              <h3 className="serif italic text-2xl md:text-3xl">{LADY.title}</h3>
              <span className="serif text-xl" data-testid="news-lady-price">{fmt(ladyMeta.price)}</span>
            </div>
            <p className="label mt-1">{LADY.medium} · {LADY.size}</p>
            <p className="mt-4 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
              The largest cowgirl in the series — emerald from crown to hem against
              a quilt of Kente, Ankara, and gold leaf. Available now; prints coming soon.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {ladyMeta.buy_url && (
                <a href={ladyMeta.buy_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-foreground text-background px-5 py-3 text-[0.7rem] uppercase tracking-[0.18em] hover:opacity-90 transition-opacity" data-testid="news-lady-buy">
                  Acquire <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              )}
              <Link to={`/work/black-western/${LADY.id}`} className="inline-flex items-center gap-2 border border-foreground px-5 py-3 text-[0.7rem] uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors" data-testid="news-lady-detail">
                View the painting
              </Link>
            </div>
          </article>
        </div>

        <Link
          to="/about#family"
          className="mt-14 md:mt-20 flex items-center justify-between gap-6 border-t border-border pt-6 group"
          data-testid="news-family-teaser"
        >
          <div className="flex items-center gap-5">
            <div className="h-14 w-14 md:h-16 md:w-16 shrink-0 overflow-hidden bg-[hsl(var(--surface))]">
              <img
                src={FAMILY_ARCHIVE.photos[0].src}
                alt={FAMILY_ARCHIVE.photos[0].alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div>
              <p className="label text-[hsl(var(--ink-soft))]">Where it comes from</p>
              <p className="serif italic text-lg md:text-2xl leading-tight">
                Three generations of Graves cowboys — Hugoton, Kansas, 1984.
              </p>
            </div>
          </div>
          <span className="label whitespace-nowrap hidden sm:inline-flex items-center gap-2 group-hover:text-foreground">
            The family story <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </Link>
      </div>
    </section>
  );
}
